<template>
  <div id="datasetDetails">
    <!-- Dataset Details -->
    <b-card
      id="datasetDetailsCard"
      class="blockDrawer large"
      :class="$store.state.drawer.datasetDetails.position"
    >
      <template v-slot:header>
        <a
          class="close clickable"
          @click="hideDatasetDetailsDrawer()"
        >
          <i class="far fa-times"></i>
        </a>
        <!-- Dataset Name -->
        <h4 v-if="$store.state.dataset.name" id="datasetTitle"><i v-if="$store.state.dataset.dynamic" class="fas fa-satellite-dish mr-1"></i>{{ $store.state.dataset.name }}</h4>
        <!-- Block Settings Button -->
        <b-btn
          v-if="$store.state.role == 'Administrator' || $store.state.role == 'Editor' || userIsEditor"
          @click="editDatasetSettings()"
          id="datasetSettings"
          class="mb-2 mr-1"
          v-b-tooltip.hover.bottom="'Dataset settings'"
        >
          <i class="fas fa-cog"></i>
        </b-btn>
        <b-btn
          v-if="$store.state.role == 'Administrator' || $store.state.role == 'Editor' || userIsEditor"
          @click="copyDataset()"
          class="mb-2 mr-1"
          variant="primary"
          v-b-tooltip.hover.bottom="'Copy Dataset'"
        >
          <i class="fal fa-copy"></i>
        </b-btn>
        <b-btn
          v-if="($store.state.role == 'Administrator' || userIsEditor) && !$store.state.dataset.dynamic && $store.state.dataset.file && !$store.state.dataset.file.raster && $store.state.datasetGeoJson && $store.state.datasetGeoJson.features && $store.state.datasetGeoJson.features.length < 2000 && $store.state.dataset.vizSettings && $store.state.dataset.vizSettings.type != 'polygon' && $store.state.dataset.vizSettings.type != 'line' && $store.state.datasetGeoJson.features[0].geometry.type !== 'Polygon' && $store.state.datasetGeoJson.features[0].geometry.type !== 'LineString' && $store.state.datasetGeoJson.features[0].geometry.type !== 'MultiLineString'"
          @click="confirmMakeDynamic = true"
          class="mb-2"
          variant="primary"
          v-b-tooltip.hover.bottom="'Make Dynamic'"
        >
          <i class="fas fa-satellite-dish"></i>
        </b-btn>
        <b-btn
          v-if="($store.state.role == 'Permission-only' || $store.state.role == 'Read-only') && !userIsEditor"
          class="mb-2"
          variant="secondary"
        >
          Read-only View: Changes will not save
        </b-btn>
      </template>

      <div class="card-top-nav">
        <ul class="nav nav-tabs">
          <li
            class="nav-item clickable"
            @click="detailsMode = 'settings'"
            v-b-tooltip.hover.bottom="'Visualize'"
          >
            <span class="nav-link"
            :class="{active:detailsMode == 'settings'}"><i class="far fa-sliders-h-square"></i></span>
          </li>
          <li
            class="nav-item clickable hide-mobile"
            @click="detailsMode = 'details'"
            v-b-tooltip.hover.bottom="'Details'"
          >
            <a class="nav-link"
            :class="{active:detailsMode == 'details'}"><i class="fas fa-info-circle"></i></a>
          </li>
          <li
            class="nav-item clickable"
            @click="detailsMode = 'list'"
            v-b-tooltip.hover.bottom="'List View'"
          >
            <span class="nav-link"
            :class="{active:detailsMode == 'list'}"><i class="fas fa-list"></i></span>
          </li>
          <li
            class="nav-item clickable hide-mobile"
            @click="detailsMode = 'downloads'"
            v-b-tooltip.hover.bottom="'Downloads'"
          >
            <a class="nav-link"
            :class="{active:detailsMode == 'downloads'}"><i class="fas fa-cloud-download-alt"></i></a>
          </li>
          <li
            class="nav-item clickable hide-mobile"
            @click="detailsMode = 'import'"
            v-b-tooltip.hover.bottom="'Import Data'"
            v-if="$store.state.dataset.dynamic && ($store.state.role == 'Administrator' || $store.state.role == 'Editor' || userIsEditor)"
          >
            <a class="nav-link"
            :class="{active:detailsMode == 'import'}"><i class="fas fa-file-import"></i></a>
          </li>
          <li
            class="nav-item clickable"
            @click="detailsMode = 'datasetEditors'"
            v-b-tooltip.hover.bottom="'Dataset Editors'"
            v-if="$store.state.role ==='Administrator'"
          >
            <a class="nav-link"
            :class="{active:detailsMode == 'datasetEditors'}"><i class="fas fa-users"></i></a>
          </li>
        </ul>
      </div><!-- /.card-top-nav -->

      <div class="mt-5">

        <div v-if="detailsMode == 'settings'">
          <p class="lead text-center" v-if="$store.state.dataset.file && $store.state.dataset.file.raster">This is a Raster Dataset and is view only</p>
          <!-- Magic Map -->
          <MagicMap v-if="$store.state.dataset.vizSettings && $store.state.dataset.vizSettings.type === 'circle'"/>
          <DatasetVizSettings />
        </div><!-- v-if settings -->

        <div v-if="detailsMode == 'details'">
          <!-- Datapoints count - only show for non-raster datasets -->
          <p v-if="$store.state.dataset.file && !$store.state.dataset.file.raster && $store.state.datasetGeoJson && $store.state.datasetGeoJson.features">
            <strong>Datapoints:</strong> {{ $store.state.datasetGeoJson.features.length }}
          </p>
          <!-- Satellite dataset info -->
          <p v-if="$store.state.dataset.file && $store.state.dataset.file.raster">
            <strong>Dataset Type:</strong> Satellite Raster
          </p>
          <p><strong>Created:</strong> {{ $store.state.dataset.createdOn | dateTime }}</p>
          <p><strong>Updated:</strong> {{ $store.state.dataset.updatedOn | dateTime }}</p>
          <!-- Dataset Description -->
          <p v-if="$store.state.dataset.description && $store.state.dataset.description.length > 0">{{ $store.state.dataset.description }}</p>
          <div v-if="$store.state.dataset.headers && $store.state.dataset.headers.length">
            <p class="mb-1 mt-3"><strong>Headers</strong></p>
            <span
              v-for="(header, index) in $store.state.dataset.headers"
              v-bind:key="index"
            >
              <span
                v-if="!isHiddenHeader(header)"
                class="badge badge-pill badge-secondary mr-1"
              >{{ header }}</span>
              <span
                v-else
                class="badge badge-pill badge-secondary mr-1"
              >{{ header }} (hidden)</span>
            </span>
          </div><!-- v-if headers -->
        </div><!-- v-if details -->

        <div v-if="detailsMode == 'list'">
          <DatasetList />
        </div><!-- /v-if detailsMode == 'list' -->

        <div v-if="detailsMode == 'downloads'">
          <p class="mb-1"><strong>Downloads</strong></p>
          <b-btn
            v-if="$store.state.dataset.file.originalFileName"
            @click="download('original')"
            class="hide-mobile btn-primary mr-1 mt-1"
          >
            <i class="far fa-file-download"></i> Original
          </b-btn>
          <b-btn
            v-if="$store.state.dataset.file && !$store.state.dataset.file.raster"
            @click="download('geojson')"
            class="hide-mobile btn-primary mr-1 mt-1"
          >
            <i class="far fa-file-download"></i> GeoJson
          </b-btn>
          <b-btn
            v-if="$store.state.dataset.file && !$store.state.dataset.file.raster"
            @click="download('csv')"
            class="hide-mobile btn-primary mr-1 mt-1"
          >
            <i class="far fa-file-download"></i> CSV
          </b-btn>
          <b-btn
            v-if="$store.state.dataset.file && !$store.state.dataset.file.raster && hasImageFields"
            @click="downloadWithImages()"
            class="hide-mobile btn-primary mr-1 mt-1"
          >
            <i class="far fa-file-download"></i> CSV + Images
          </b-btn>
          <b-btn
            v-if="hasLogFields"
            @click="downloadLogs()"
            :disabled="downloadingLogs"
            class="hide-mobile btn-primary mr-1 mt-1"
            v-b-tooltip.hover.bottom="'Every logged reading over time, one row per entry. Logged images export as download links.'"
          >
            <i class="far" :class="downloadingLogs ? 'fa-spinner fa-spin' : 'fa-file-download'"></i>
            {{ downloadingLogs ? 'Preparing…' : 'Logs CSV' }}
          </b-btn>
          <b-btn
            @click="downloadZoneStats()"
            class="btn-primary mr-1 mt-1"
            v-if="$store.state.dataset.vizSettings && $store.state.dataset.vizSettings.colorBy === 'valueBased' && $store.state.dataset.vizSettings.zoneClassification !== 'linear' && $store.state.dataset.vizSettings.zoneClassification !== 'categorical'"
          >
            <i class="far fa-file-download"></i> Zone Stats CSV
          </b-btn>
          <b-btn
            @click="downloadZonePolygons()"
            class="btn-primary mr-1 mt-1"
            v-if="$store.state.dataset.vizSettings && $store.state.dataset.vizSettings.colorBy === 'valueBased' && $store.state.dataset.vizSettings.zoneClassification !== 'linear' && $store.state.dataset.vizSettings.zoneClassification !== 'categorical' && $store.state.datasetGeoJson && $store.state.datasetGeoJson.features && $store.state.datasetGeoJson.features.length && $store.state.datasetGeoJson.features[0].geometry.type === 'Point'"
          >
            <i class="far fa-file-download"></i> Zone Polygons Shapefile
          </b-btn>
          <b-btn
            @click="downloadPolygonsShapefile()"
            class="btn-primary mr-1 mt-1"
            v-if="$store.state.dataset.vizSettings && $store.state.dataset.vizSettings.type === 'polygon'"
          >
            <i class="far fa-file-download"></i> Polygons Shapefile
          </b-btn>
          <b-btn
            @click="print()"
            class="hide-mobile btn-primary mr-1 mt-1"
          >
            <i class="fas fa-print"></i> Print
          </b-btn>
          
          <!-- GeoJSON API Endpoint Display -->
          <div v-if="$store.state.dataset.file && !$store.state.dataset.file.raster && $store.state.dataset.file.geoJsonPath" class="mt-3">
            <p class="mb-2"><strong>Public GeoJSON API Endpoint:</strong></p>
            <b-form-checkbox
              :checked="$store.state.dataset.public"
              @change="togglePublic($event)"
              switch
              :disabled="$store.state.role !== 'Administrator' && $store.state.role !== 'Editor' && $store.state.farm.ownerId !== $store.state.currentUser.uid"
            >
              {{ $store.state.dataset.public ? 'Public — anyone with the URL can read this dataset' : 'Private — only accessible via API token' }}
            </b-form-checkbox>
            <div v-if="$store.state.dataset.public" class="mt-2">
              <div class="input-group">
                <input
                  type="text"
                  class="form-control"
                  :value="geojsonApiUrl"
                  readonly
                  ref="geojsonApiUrlInput"
                >
                <div class="input-group-append">
                  <b-btn
                    @click="copyGeojsonApiUrl()"
                    variant="outline-secondary"
                    v-b-tooltip.hover.bottom="'Copy API Endpoint'"
                  >
                    <i class="far fa-copy"></i>
                  </b-btn>
                </div>
              </div>
              <small class="text-muted">This read-only endpoint is accessible without authentication.</small>
            </div>
            <div v-else class="mt-1">
              <small class="text-muted">Enable public access to share this endpoint, or use the <strong>Developer API</strong> with an API token for authenticated access.</small>
            </div>
          </div>
        </div><!-- v-if downloads -->
        <div v-if="detailsMode == 'import'">
          <ImportData />
        </div><!-- v-if import -->
        <div v-if="detailsMode == 'datasetEditors'">
          <DatasetEditors />
        </div>
      </div><!-- margin top -->

    </b-card>
    <!-- Info toggle -->
    <b-btn
      v-b-tooltip.hover.right="'Dataset Details'"
      @click="$store.commit('showDrawer', 'datasetDetails')"
      class="info_toggle"
      variant="primary"
    >
      <span class="desktop-toggle">
        <i class="fas fa-caret-left"></i> <i class="fas fa-chart-bar"></i>
      </span>
      <span class="mobile-toggle">
        <i class="fas fa-caret-up"></i> Details
      </span>
    </b-btn>
    <Confirm
      v-if="confirmMakeDynamic"
      text="Are you sure you want to make this dataset dynamic? This will generate a new collector and will allow data to be added, changed, and removed from this dataset. A copy of this dataset will be placed in your trash folder."
      v-on:confirmed="makeDynamic($event)"
    />
  </div><!-- /#datasetDetails -->
</template>

<script>
import Confirm from '../Confirm'
import DatasetEditors from './DatasetEditors.vue'
import DatasetList from './DatasetList.vue'
import ImportData from './ImportData.vue'
import moment from 'moment'
import firebase from 'firebase/compat/app'
import axios from 'axios'
import shpwrite from 'shp-write'
import DatasetVizSettings from './DatasetVizSettings'
import MagicMap from './MagicMap.vue'
import Papa from 'papaparse'
import JSZip from 'jszip'
import { getAllCollectorLogs } from '../../services/HeaderLogService'
// import e from 'express'
const turf = require('@turf/turf')
const fb = require('../../firebaseConfig.js')

export default {
  data () {
    return {
      detailsMode: 'settings',
      confirmMakeDynamic: false,
      userIsEditor: false,
      downloadingLogs: false
    }
  },
  components: {
    Confirm,
    DatasetEditors,
    DatasetList,
    ImportData,
    DatasetVizSettings,
    MagicMap
  },
  created () {
    this.checkUser()
  },

  computed: {
    collector: {
      get () {
        return this.$store.state.collector
      },
      set (value) {
        this.$store.commit('setCollector', value)
      }
    },
    geojsonApiUrl () {
      if (this.$store.state.dataset.file && this.$store.state.dataset.file.geoJsonPath && !this.$store.state.dataset.file.raster) {
        return `https://api.every.farm/dataset/${this.$store.state.dataset.id}/geojson`
      }
      return ''
    },
    hasImageFields () {
      if (this.$store.state.dataset.dynamic && this.$store.state.collector.fields) {
        return this.$store.state.collector.fields.some(field =>
          field.type === 'Image' || field.type === 'Computer Vision'
        )
      }
      return false
    },
    // True when this dynamic dataset has at least one header that logs values
    // over time — gates the "Logs CSV" export.
    hasLogFields () {
      if (this.$store.state.dataset.dynamic && this.$store.state.collector.fields) {
        return this.$store.state.collector.fields.some(field => field.isLogEnabled === true)
      }
      return false
    }
  },
  methods: {
    isHiddenHeader (val) {
      if (this.$store.state.dataset.dynamic) {
        let isHidden = false
        this.$store.state.collector.fields.forEach(header => {
          if (header.hidden && header.label === val) {
            isHidden = true
          }
        })
        return isHidden
      } else {
        return false
      }
    },
    async downloadLogs () {
      const collector = this.$store.state.collector
      const collectorId = collector && collector.id
      if (!collectorId) {
        this.$store.commit('setAlert', { active: true, message: 'No collector found for this dataset.', level: 'alert-danger', timeout: 4000 })
        return
      }
      this.downloadingLogs = true
      try {
        const entries = await getAllCollectorLogs({ collectorId })
        if (!entries.length) {
          this.$store.commit('setAlert', { active: true, message: 'No log entries to export yet.', level: 'alert-info', timeout: 4000 })
          return
        }
        // machine_name -> label, so the CSV uses human-readable header names.
        const labelByMachine = {}
        ;(collector.fields || []).forEach(f => { labelByMachine[f.machine_name] = f.label })

        // Resolve download URLs for any logged images so the CSV links to the
        // actual photos instead of just counting them.
        const imageUrlsByEntry = await this.resolveLogImageUrls(entries)

        const rows = entries.map(e => {
          const ts = e.timestamp && e.timestamp.toDate ? e.timestamp.toDate() : null
          return {
            dataPointId: e.dataPointId || '',
            header: e.headerLabel || labelByMachine[e.headerMachineName] || e.headerMachineName || '',
            timestamp: ts ? ts.toISOString() : '',
            value: this.formatLogValueForCsv(e.value, imageUrlsByEntry[e.id]),
            notes: e.notes || '',
            userId: e.userId || ''
          }
        })

        const csv = Papa.unparse(rows, { columns: ['dataPointId', 'header', 'timestamp', 'value', 'notes', 'userId'] })
        const cleanName = (this.$store.state.dataset.name || 'dataset').replace(/\s+/g, '_')
        const blob = new Blob([csv], { type: 'text/csv' })
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = cleanName + '_logs.csv'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(a.href)
      } catch (err) {
        console.log(err.message)
        this.$store.commit('setAlert', { active: true, message: 'Could not export logs: ' + (err.message || err), level: 'alert-danger', timeout: 5000 })
      } finally {
        this.downloadingLogs = false
      }
    },
    // Flatten a log value to a single CSV cell. Datetime -> ISO; image values
    // -> resolved download URL(s) so the photos are retrievable from the
    // export (falls back to a count if a URL couldn't be resolved).
    formatLogValueForCsv (v, imageUrls) {
      if (v === undefined || v === null) return ''
      if (typeof v.toDate === 'function') return v.toDate().toISOString()
      if (this.isImageLogValue(v)) {
        if (imageUrls && imageUrls.length) return imageUrls.join(' ; ')
        const arr = Array.isArray(v) ? v : [v]
        const count = arr.filter(img => img && typeof img === 'object').length
        return count ? count + (count === 1 ? ' image' : ' images') : ''
      }
      if (typeof v === 'object') return JSON.stringify(v)
      return v
    },
    isImageLogValue (v) {
      if (Array.isArray(v)) return v.some(img => img && typeof img === 'object' && (img.path || img.url || img.small || img.large))
      return !!(v && typeof v === 'object' && (v.path || v.url || v.small || v.large))
    },
    // Build { entryId: [url, ...] } for every image log entry. Prefers an
    // already-stored url, dedupes by storage path, and caps concurrency so a
    // big export doesn't fire hundreds of Storage requests at once.
    async resolveLogImageUrls (entries) {
      const out = {}
      const pathCache = {}
      const tasks = []
      entries.forEach(e => {
        if (!this.isImageLogValue(e.value)) return
        out[e.id] = []
        const imgs = Array.isArray(e.value) ? e.value : [e.value]
        imgs.forEach(img => {
          if (!img || typeof img !== 'object') return
          if (img.url) { out[e.id].push(img.url); return }
          const path = img.large || img.path || img.small
          if (path) tasks.push({ entryId: e.id, path })
        })
      })
      const CONCURRENCY = 10
      for (let i = 0; i < tasks.length; i += CONCURRENCY) {
        const batch = tasks.slice(i, i + CONCURRENCY)
        await Promise.all(batch.map(async t => {
          try {
            if (!pathCache[t.path]) {
              pathCache[t.path] = await firebase.storage().ref(t.path).getDownloadURL()
            }
            out[t.entryId].push(pathCache[t.path])
          } catch (err) { /* unresolved image — skip its URL */ }
        }))
      }
      return out
    },
    downloadPolygonsShapefile () {
      console.log('Download Polygons Shapefile')
      // Dataset GeoJson
      const datasetGeoJson = this.$store.state.datasetGeoJson
      console.log(datasetGeoJson)
      // Download SHP
      const shpName = this.$store.state.dataset.name.replace(/\s+/g, '_')
      const downloadOptions = {
        folder: shpName + '_shp',
        types: {
          point: shpName + '_points',
          polygon: shpName + '_polygons',
          line: shpName + '_lines'
        }
      }
      shpwrite.download(datasetGeoJson, downloadOptions)
    },
    downloadZonePolygons () {
      const datasetGeoJson = this.$store.state.datasetGeoJson
      if (!datasetGeoJson.features.length || datasetGeoJson.features[0].geometry.type !== 'Point') {
        this.$store.commit('setAlert', { active: true, message: 'Zone polygons can only be generated from point datasets.', level: 'alert-danger', timeout: 5000 })
        return
      }
      console.log('Download Zone Polygons')
      const vizSettings = this.$store.state.dataset.vizSettings
      console.log(vizSettings)
      // Dataset GeoJson
      console.log(datasetGeoJson)
      // Generate bounding box
      const bbox = turf.bbox(datasetGeoJson)
      console.log(bbox)
      // Generate concave hull
      const hull = turf.concave(datasetGeoJson, { units: 'kilometers', maxEdge: 0.05 })
      console.log(hull)
      const bufferedHull = turf.buffer(hull, 0.003, { units: 'kilometers' })
      // Generate Voronoi Polygons in place of points
      const voronoiPolygons = turf.voronoi(datasetGeoJson, { bbox: bbox })
      // Attach properties to those polygons
      for (let i = 0; i < datasetGeoJson.features.length; i++) {
        voronoiPolygons.features[i].properties = datasetGeoJson.features[i].properties
      }
      console.log(voronoiPolygons)
      // Test all coordinate pairs to see if they are within the hull. Splice out if not
      voronoiPolygons.features.forEach(feature => {
        let wasTrimmed = false
        for (let i = feature.geometry.coordinates[0].length - 1; i >= 0; i--) {
          const pointToCheck = turf.point(feature.geometry.coordinates[0][i])
          const isInside = turf.booleanPointInPolygon(pointToCheck, bufferedHull)
          if (!isInside) {
            feature.geometry.coordinates[0].splice(i, 1)
            wasTrimmed = true
          }
        }
        if (wasTrimmed) {
          feature.geometry.coordinates[0].push(feature.geometry.coordinates[0][0])
        }
      })
      // Prepare empty zones
      const zones = {}
      if (vizSettings.categorical) {
        for (let i = 0; i < vizSettings.featureCategories.length; i++) {
          zones[vizSettings.featureCategories[i].name] = []
        }
      } else {
        for (let i = 0; i < vizSettings.zoneStops; i++) {
          zones[i] = []
        }
      }
      // push each polygon feature into the appropriate zone
      voronoiPolygons.features.forEach(polygon => {
        if (polygon.geometry.coordinates[0].length > 3) { // Polygons must have at least 4 coodinate pairs
          if (vizSettings.categorical) {
            zones[polygon.properties[vizSettings.colorHeader]].push(polygon)
          } else if (vizSettings.zoneClassification === 'equal-interval' || vizSettings.zoneClassification === 'jenks') {
            for (let i = 0; i < vizSettings.zoneStops; i++) {
              const zoneMinMax = {}
              if (vizSettings.zoneClassification === 'equal-interval') {
                const stopDiff = vizSettings.maxValue - vizSettings.minValue
                const stopLength = 1 / vizSettings.zoneStops
                const stopMultiplierMin = stopLength * i
                const stopMultiplierMax = stopLength * (i + 1)
                zoneMinMax.min = (stopDiff * stopMultiplierMin) + vizSettings.minValue
                zoneMinMax.max = (stopDiff * stopMultiplierMax) + vizSettings.minValue
              }
              if (vizSettings.zoneClassification === 'jenks') {
                zoneMinMax.min = vizSettings.breaks[i]
                zoneMinMax.max = vizSettings.breaks[i + 1]
              }
              if (i === vizSettings.zoneStops - 1) { // if last zone include the max value in the zone
                if (polygon.properties[vizSettings.colorHeader] >= zoneMinMax.min && polygon.properties[vizSettings.colorHeader] <= zoneMinMax.max) {
                  zones[i].push(polygon)
                }
              } else {
                if (polygon.properties[vizSettings.colorHeader] >= zoneMinMax.min && polygon.properties[vizSettings.colorHeader] < zoneMinMax.max) {
                  zones[i].push(polygon)
                }
              }
            }
          }
        }
      })
      // Combine polygons
      const zoneKeys = Object.keys(zones)
      const zonesGeoJson = {
        type: 'FeatureCollection',
        features: []
      }
      zoneKeys.forEach(key => {
        let zoneUnion = false
        zones[key].forEach((zoneKeyFeature, i) => {
          if (!zoneUnion) {
            zoneUnion = zoneKeyFeature
          } else {
            zoneUnion = turf.union(zoneUnion, zoneKeyFeature)
          }
        })
        console.log(zoneUnion)
        zoneUnion.properties = {
          zone: key
        }
        zonesGeoJson.features.push(zoneUnion)
      })
      // Change all 'MultiPolygon' to 'Polygon'
      // zonesGeoJson.features.forEach(zoneGeoJsonFeature => {
      //   zoneGeoJsonFeature.geometry.type = 'Polygon'
      // })
      // const flatZonesGeoJson = turf.flatten(zonesGeoJson)
      console.log(zonesGeoJson)
      // console.log(flatZonesGeoJson)
      // Download SHP
      const shpName = this.$store.state.dataset.name.replace(/\s+/g, '_') + '_kmeans'
      const downloadOptions = {
        folder: shpName + '_shp',
        types: {
          point: shpName + '_points',
          polygon: shpName + '_polygons',
          line: shpName + '_lines'
        }
      }
      shpwrite.download(zonesGeoJson, downloadOptions)

      // Download for now
      // var a = document.createElement('a')
      // a.href = URL.createObjectURL(
      //   new Blob([JSON.stringify(zonesGeoJson)], { type: 'application/json' })
      // )
      // a.download = 'voronoi.geojson'
      // a.click()
    },
    checkUser () {
      if (this.$store.state.dataset.editors && this.$store.state.dataset.editors.includes(this.$store.state.currentUser.uid)) {
        this.userIsEditor = true
      }
    },
    makeDynamic (confirm) {
      this.confirmMakeDynamic = false
      const collectorFields = []
      let collectorId = ''
      let newDatasetId = ''
      // let collectorData = {}

      const self = this
      async function addDataPoint (feature) {
        const dataPointData = {
          accuracy: 1,
          createdBy: self.$store.state.currentUser.uid,
          createdOn: new Date(),
          geolocation: new firebase.firestore.GeoPoint(feature.geometry.coordinates[1], feature.geometry.coordinates[0]),
          updatedBy: self.$store.state.currentUser.uid,
          updatedOn: new Date()
        }
        collectorFields.forEach(field => {
          dataPointData[field.machine_name] = feature.properties[field.label] || ''
        })
        await fb.collectorsCollection.doc(collectorId).collection('dataPoints').add(dataPointData).then(() => {
          return true
        }).catch(err => {
          console.log(err)
          return true
        })
      }

      if (confirm) {
        console.log(this.$store.state)
        this.$store.commit('setAlert', { active: true, message: 'Starting converstion. This can take a while.', level: 'alert-warning' })
        // Set map loader
        this.$store.commit('setMapLoader', true)
        // Hide dataset details drawer
        this.$store.commit('tuckDrawer', 'datasetDetails')
        // Gather fields
        this.$store.state.dataset.headers.forEach(header => {
          const collectorField = {}
          collectorField.label = header
          const snakeCase = string => {
            string = string.replace(/\W+/g, ' ').toLowerCase().split(' ').join('_')
            if (string.charAt(string.length - 1) === '_') {
              return string.substring(0, string.length - 1)
            }
            return string
          }
          const sampleValue = this.$store.state.datasetGeoJson.features[0].properties[header]
          if (typeof sampleValue === 'boolean' || isNaN(sampleValue)) {
            collectorField.type = 'Text'
          } else {
            collectorField.type = 'Number'
          }
          // Grid-generator bookkeeping columns (UTM coordinates, cell ids,
          // spacing flags) ride along on sampling grids. They are not
          // things a person enters in the field — leaving them as normal
          // fields made the Collector app demand "valid data" for
          // utmEasting/utmNorthing on every point. Keep the values, hide
          // the fields from the form.
          const GRID_METADATA = ['utmeasting', 'utmnorthing', 'utmzone', 'gridspacing', 'decimalprecision', 'zoneforced', 'cellid', 'utmx', 'utmy', 'cellsize', 'row', 'col']
          if (GRID_METADATA.includes(String(header).toLowerCase())) {
            collectorField.hidden = true
          }
          collectorField.machine_name = snakeCase(header)
          if (collectorField.machine_name === 'dataPointId' || collectorField.machine_name === 'datapointid') {
            collectorField.machine_name = 'dataPointId_field'
          }
          collectorField.min = false
          collectorField.max = false
          collectorField.step = false
          collectorField.suffix = ''
          collectorField.required = false
          collectorFields.push(collectorField)
        })

        // Create Collector
        this.collector = {
          createdOn: new Date(),
          updatedOn: new Date(),
          createdBy: this.$store.state.currentUser.uid,
          updatedBy: this.$store.state.currentUser.uid,
          ownerId: this.$store.state.currentUser.uid,
          name: this.$store.state.dataset.name,
          description: this.$store.state.dataset.description || '',
          fields: collectorFields,
          farmId: this.$store.state.farm.id,
          trashed: false,
          reCompile: true
        }
        fb.collectorsCollection.add(this.collector).then(collectorDoc => {
          // const collectorItem = []
          // collectorItem.id = collectorDoc.id
          // const datasetHeaders = []
          // this.collectorItem.fields.forEach(field => {
          //   datasetHeaders.push(field.label)
          // })
          // Create dynamic dataset
          collectorId = collectorDoc.id
          // this.collector = collectorDoc.data()
          this.collector.id = collectorDoc.id
          return fb.datasetsCollection.add({
            dynamic: true,
            collectorId: collectorDoc.id,
            createdOn: new Date(),
            updatedOn: new Date(),
            createdBy: this.$store.state.currentUser.uid,
            updatedBy: this.$store.state.currentUser.uid,
            name: this.$store.state.dataset.name || '',
            description: this.$store.state.dataset.description || '',
            headers: this.$store.state.dataset.headers || [],
            farmId: this.$store.state.farm.id,
            file: {},
            folderId: 'root'
          })
        }).then(datasetDoc => {
          // const datasetItem = datasetDoc.data()
          // datasetItem.id = datasetDoc.id
          newDatasetId = datasetDoc.id
          this.collector.datasetId = datasetDoc.id
          return fb.collectorsCollection.doc(collectorId).update({
            datasetId: datasetDoc.id,
            updatedOn: new Date(),
            updatedBy: this.$store.state.currentUser.uid
          })
        }).then(() => {
          // Populate Collector
          console.log('Blank collector and dataset should have been created!')
          this.$store.commit('setAlert', { active: true, message: 'Collector and dataset generated. Adding new data points. Please hold tight.', level: 'alert-warning' })
          const updatePromises = []
          this.$store.state.datasetGeoJson.features.forEach(feature => {
            const prom = addDataPoint(feature)
            updatePromises.push(prom)
            // const dataPointData = {
            //   accuracy: 1,
            //   createdBy: this.$store.state.currentUser.uid,
            //   createdOn: new Date(),
            //   geolocation: new firebase.firestore.GeoPoint(feature.geometry.coordinates[1], feature.geometry.coordinates[0]),
            //   updatedBy: this.$store.state.currentUser.uid,
            //   updatedOn: new Date()
            // }
            // collectorFields.forEach(field => {
            //   dataPointData[field.machine_name] = feature.properties[field.label] || ''
            // })
            // fb.collectorsCollection.doc(collectorId).collection('dataPoints').add(dataPointData)
          })
          return Promise.all(updatePromises)
        }).then(() => {
          // Compile new dataset
          console.log('Compile here')
          return fb.compileRequestsCollection.add({
            createdBy: this.$store.state.currentUser.uid,
            createdOn: new Date(),
            collectorId: collectorId,
            datasetId: newDatasetId,
            lastUpdated: new Date(),
            farmId: this.$store.state.farm.id
          })
        }).then(() => {
          console.log('Ready')
          // Place old dataset into trash
          fb.datasetsCollection.doc(this.$store.state.dataset.id).update({
            folderId: 'trash',
            updatedBy: this.$store.state.currentUser.uid,
            updatedOn: new Date()
          })
          // Remove map loader
          this.$store.commit('setMapLoader', false)
          this.$store.commit('setAlert', { active: true, message: 'Successfully converted dataset to dynamic!', level: 'alert-success', timeout: 3000 })
          this.$store.dispatch('refreshFarm').then(() => {
            console.log('farm refreshed, not take us to the collector settings')
            // Open the Collectors Drawer
            this.$store.commit('hideAllDrawers')
            setTimeout(() => {
              this.$store.commit('showDrawer', 'collectors')
            }, 300)
            // Open the 'Create Data Collector' Settings window
            setTimeout(() => {
              // this.collector = collectorData
              this.$store.commit('setEditMode', true)
              this.$store.commit('toggleModal', 'createCollector')
            }, 1000)
          })
        }).catch(err => {
          console.log(err)
        })
      }
    },
    print () {
      // this.$store.commit('setDrawer', { name: 'mapDetails', position: 'out' })
      this.$store.commit('hideDrawer', 'datasetDetails')
      const self = this
      var h1 = document.createElement('H1')
      // textContent (not innerHTML) so a malicious dataset name like
      // `<img src=x onerror="...">` can't fire JS when the print
      // header gets appended to the document.
      h1.textContent = this.$store.state.dataset.name
      h1.id = 'printHeader'
      document.body.appendChild(h1)
      setTimeout(() => {
        self.$store.commit('setStateProperty', { property: 'printingClass', value: 'printing' })
        document.body.style.backgroundColor = 'white'
        const map = self.$store.state.mapObject
        setTimeout(() => {
          map.resize()
          setTimeout(() => {
            window.print()
            self.$store.commit('setStateProperty', { property: 'printingClass', value: 'null' })
            document.body.style.backgroundColor = 'black'
            // self.$store.commit('setDrawer', { name: 'mapDetails', position: 'in' })
            this.$store.commit('showDrawer', 'datasetDetails')
            const element = document.getElementById('printHeader')
            element.remove()
          }, 500)
        }, 500)
      }, 1000)
    },
    copyDataset () {
      this.$store.commit('hideDrawer', 'datasetDetails')
      setTimeout(() => {
        this.$store.commit('showDrawer', 'copyDataset')
      }, 300)
    },
    hideDatasetDetailsDrawer () {
      this.$store.commit('tuckDrawer', 'datasetDetails')
    },
    downloadZoneStats () {
      console.log('Zone Stats CSV')
      const zoneStatsData = []
      const hull = turf.concave(this.$store.state.datasetGeoJson, { units: 'kilometers', maxEdge: 0.05 })
      const area = turf.area(hull)
      const totalPointsCount = this.$store.state.datasetGeoJson.features.length
      const vizSettings = this.$store.state.dataset.vizSettings
      for (let i = 0; i < vizSettings.zoneStops; i++) {
        const itemObj = {
          label: 'Zone ' + (i + 1)
        }
        if (vizSettings.zoneClassification === 'equal-interval') {
          const stopDiff = vizSettings.maxValue - vizSettings.minValue
          const stopLength = 1 / vizSettings.zoneStops
          const stopMultiplierMin = stopLength * i
          const stopMultiplierMax = stopLength * (i + 1)
          itemObj.min = (stopDiff * stopMultiplierMin) + vizSettings.minValue
          itemObj.max = (stopDiff * stopMultiplierMax) + vizSettings.minValue
        }
        if (vizSettings.zoneClassification === 'jenks') {
          itemObj.min = parseFloat(vizSettings.breaks[i].toFixed(8))
          itemObj.max = parseFloat(vizSettings.breaks[i + 1].toFixed(8))
        }
        var zonePointsCount = 0
        this.$store.state.datasetGeoJson.features.forEach((feature, i) => {
          // Note this small issues: the highest value in the dataset will never be counted toward the convace hull using this if statement
          if (feature.properties[this.$store.state.dataset.vizSettings.colorHeader] >= itemObj.min && feature.properties[this.$store.state.dataset.vizSettings.colorHeader] < itemObj.max) {
            zonePointsCount++
          }
        })
        const countPercent = zonePointsCount / totalPointsCount
        const zoneArea = area * countPercent
        // itemObj.area = zoneArea
        const features = this.$store.state.datasetGeoJson.features
        const header = this.$store.state.dataset.vizSettings.colorHeader
        features.sort((a, b) => a.properties[header] - b.properties[header])
        const statsValues = []
        features.forEach(feature => {
          const value = feature.properties[header]
          if (value >= itemObj.min && value <= itemObj.max) {
            statsValues.push(feature.properties[header])
          }
        })
        const half = Math.floor(statsValues.length / 2)
        if (statsValues.length % 2) {
          itemObj.median = parseFloat(statsValues[half].toFixed(8))
        } else {
          itemObj.median = parseFloat(((statsValues[half - 1] + statsValues[half]) / 2.0).toFixed(8))
        }
        const sum = statsValues.reduce((a, b) => a + b, 0)
        itemObj.mean = parseFloat((sum / statsValues.length).toFixed(8)) || 0
        itemObj.count = statsValues.length
        itemObj.sum = parseFloat(sum.toFixed(8))
        // stats.min = this.$store.state.keyStatsMin
        // stats.max = this.$store.state.keyStatsMax
        itemObj.σ = parseFloat(Math.sqrt(statsValues.map(x => Math.pow(x - itemObj.mean, 2)).reduce((a, b) => a + b) / itemObj.count).toFixed(8))
        if (this.$store.state.userProfile.measurementSystem === 'Imperial') {
          itemObj.acres = parseFloat((zoneArea * 0.000247105).toFixed(8))
        } else if (this.$store.state.userProfile.measurementSystem === 'Metric') {
          itemObj.hectares = parseFloat((zoneArea / 10000).toFixed(8))
        }
        zoneStatsData.push(itemObj)
      }
      console.log(zoneStatsData)

      const replacer = (key, value) => value === null ? '' : value // specify how you want to handle null values here
      const header = Object.keys(zoneStatsData[0])
      let csv = zoneStatsData.map(row => header.map(fieldName => JSON.stringify(row[fieldName], replacer)).join(','))
      csv.unshift(header.join(','))
      csv = csv.join('\r\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const urlObj = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = urlObj
      a.download = this.$store.state.dataset.name.replace(/\s+/g, '_') + '_zones.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    download (option) {
      let path
      let fileName
      let cleanDescription
      if (this.$store.state.dataset.file.originalFileName) {
        cleanDescription = this.$store.state.dataset.file.originalFileName
      } else {
        cleanDescription = this.$store.state.dataset.name.replace(/\s+/g, '_')
      }
      if (option === 'original') {
        path = this.$store.state.dataset.file.path
        fileName = cleanDescription
      }
      if (option === 'geojson' || option === 'csv') {
        path = this.$store.state.dataset.file.geoJsonPath
        if (option === 'geojson') {
          fileName = cleanDescription + '.geojson'
        } else if (option === 'csv') {
          fileName = cleanDescription + '.csv'
        }
      }
      if (option === 'shp') {
        path = this.$store.state.dataset.file.geoJsonPath
        fileName = cleanDescription
      }

      const storageRef = firebase.storage().ref(path)

      if (option === 'original' || option === 'geojson') {
        storageRef.getDownloadURL().then(url => {
          return fetch(url)
        }).then(response => {
          return response.blob()
        }).then(blob => {
          return URL.createObjectURL(blob)
        }).then(object => {
          const a = document.createElement('a')
          a.href = object
          a.download = fileName
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
        }).catch(err => {
          console.log(err)
        })
      } else if (option === 'csv') {
        storageRef.getDownloadURL().then(url => {
          return axios.get(url)
        }).then(response => {
          const geoJsonData = response.data
          const items = []
          const itemHeaders = []
          // Export in collection order (createdOn) — compiled files written
          // before the compile-order fix are in random document-id order.
          const orderedFeatures = (geoJsonData.features || []).slice().sort((a, b) => {
            const ta = (a.properties && Number(a.properties.createdOn)) || 0
            const tb = (b.properties && Number(b.properties.createdOn)) || 0
            return ta - tb
          })
          orderedFeatures.forEach(feature => {
            const jsonObject = feature.properties
            if (jsonObject.createdOn) {
              const date = jsonObject.createdOn * 1000
              jsonObject.createdOn = moment(date).format('YYYY-MM-DD HH:mm:ss')
            }
            if (jsonObject.updatedOn) {
              const date = jsonObject.updatedOn * 1000
              jsonObject.updatedOn = moment(date).format('YYYY-MM-DD HH:mm:ss')
            }
            // FORMAT AY DATE AND TIME FIELDS
            if (this.$store.state.dataset.dynamic) {
              this.$store.state.collector.fields.forEach(field => {
                if (field.type === 'Date and Time' && feature.properties[field.label]) {
                  const headerDate = feature.properties[field.label] * 1000
                  if (field.includeTime) {
                    feature.properties[field.label] = moment(headerDate).format('YYYY-MM-DD HH:mm:ss')
                  } else {
                    feature.properties[field.label] = moment(headerDate).format('YYYY-MM-DD')
                  }
                }
              })
            }
            jsonObject.longitude = feature.geometry.coordinates[0]
            jsonObject.latitude = feature.geometry.coordinates[1]
            // KEEP TRACK OF KEYS IN CASE SOME FEATURES DON'T HAVE ALL KEYS. WE'LL PUSH EMPTY VALUES TO THOSE LATER
            const jsonKeys = Object.keys(jsonObject)
            jsonKeys.forEach(key => {
              if (!itemHeaders.includes(key)) {
                itemHeaders.push(key)
              }
            })
            items.push(jsonObject)
          })
          // Look to see if dynamic and if there are any image fields. If so, remove them.
          if (this.$store.state.dataset.dynamic) {
            let imageField = false
            this.$store.state.collector.fields.forEach(field => {
              if (field.type === 'Image' || field.type === 'Computer Vision') {
                imageField = field
              }
            })
            if (imageField) {
              items.forEach(item => {
                if (item[imageField.label] && item[imageField.label].length > 0) {
                  item[imageField.label] = 'Images stored on Every.Farm'
                } else {
                  item[imageField.label] = 'No images collected'
                }
              })
            }
          }
          // ENSURE THAT ALL ITEM HEADERS EXIST IN EACH RECORD
          items.forEach(item => {
            itemHeaders.forEach(header => {
              if (!item[header]) {
                item[header] = null
              }
            })
          })
          const csv = Papa.unparse(items)
          const blob = new Blob([csv], { type: 'text/csv' })
          return URL.createObjectURL(blob)
        }).then(object => {
          const a = document.createElement('a')
          a.href = object
          a.download = fileName
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
        }).catch(err => {
          console.log(err.message)
        })
      } else if (option === 'shp') {
        storageRef.getDownloadURL().then(url => {
          return axios.get(url)
        }).then(response => {
          const geoJsonData = response.data
          const downloadOptions = {
            folder: cleanDescription + '_' + option,
            types: {
              point: cleanDescription + '_points',
              polygon: cleanDescription + '_polygons',
              line: cleanDescription + '_lines'
            }
          }
          shpwrite.download(geoJsonData, downloadOptions)
        })
      }
    },
    editDatasetSettings () {
      // this.$store.commit('setDrawer', { name: 'datasetDetails', position: 'out' })
      this.$store.commit('tuckDrawer', 'datasetDetails')
      this.$store.commit('setEditMode', true)
      // this.$store.commit('setStateProperty', { property: 'showCreateDatasetDrawer', value: true })
      setTimeout(() => {
        this.$store.commit('showDrawer', 'createDataset')
      }, 300)
      // const self = this
      // setTimeout(function () {
      //   // self.$store.commit('toggleModal', 'createDataset')
      //   self.$store.commit('setDrawer', { name: 'createDatasetDrawer', position: 'in' })
      // }, 300)
    },
    togglePublic (isPublic) {
      const datasetId = this.$store.state.dataset.id
      fb.datasetsCollection.doc(datasetId).update({
        public: isPublic
      }).then(() => {
        this.$store.state.dataset.public = isPublic
        this.$store.commit('setAlert', {
          active: true,
          message: isPublic ? 'Dataset is now public.' : 'Dataset is now private.',
          level: 'alert-success',
          timeout: 3000
        })
      }).catch(err => {
        this.$store.commit('setAlert', {
          active: true,
          message: err.message,
          level: 'alert-warning',
          timeout: 3000
        })
      })
    },
    async copyGeojsonApiUrl () {
      try {
        if (this.geojsonApiUrl && this.geojsonApiUrl !== 'Error generating endpoint') {
          // Copy the API endpoint to clipboard
          if (navigator.clipboard) {
            await navigator.clipboard.writeText(this.geojsonApiUrl)
            this.$store.commit('setAlert', { 
              active: true, 
              message: 'GeoJSON API endpoint copied to clipboard! Returns data directly.', 
              level: 'alert-success', 
              timeout: 3000 
            })
          } else {
            // Fallback for older browsers
            const textArea = document.createElement('textarea')
            textArea.value = this.geojsonApiUrl
            document.body.appendChild(textArea)
            textArea.select()
            document.execCommand('copy')
            document.body.removeChild(textArea)
            this.$store.commit('setAlert', { 
              active: true, 
              message: 'GeoJSON API endpoint copied to clipboard! Returns data directly.', 
              level: 'alert-success', 
              timeout: 3000 
            })
          }
        } else {
          this.$store.commit('setAlert', { 
            active: true, 
            message: 'No API endpoint available for this dataset.', 
            level: 'alert-warning', 
            timeout: 5000 
          })
        }
      } catch (error) {
        console.error('Error copying API endpoint:', error)
        this.$store.commit('setAlert', { 
          active: true, 
          message: 'Error copying API endpoint. Please try again.', 
          level: 'alert-danger', 
          timeout: 5000 
        })
      }
    },
    async downloadWithImages () {
      console.log('Starting CSV + Images download...')
      
      // Show loading alert
      this.$store.commit('setAlert', { 
        active: true, 
        message: 'Preparing CSV + Images download... This may take a few moments.', 
        level: 'alert-info', 
        timeout: 0 
      })
      
      try {
        // Get the GeoJSON data
        const path = this.$store.state.dataset.file.geoJsonPath
        const storageRef = firebase.storage().ref(path)
        const url = await storageRef.getDownloadURL()
        const response = await axios.get(url)
        const geoJsonData = response.data
        
        // Check for very large datasets and warn user
        const totalFeatures = geoJsonData.features.length
        if (totalFeatures > 1000) {
          const confirmed = confirm(
            'This dataset has ' + totalFeatures + ' records, which may result in a very large download (potentially thousands of images). ' +
            'This could take a long time and may cause browser memory issues. ' +
            'Consider using the command-line tool for large datasets. Continue anyway?'
          )
          if (!confirmed) {
            this.$store.commit('setAlert', { 
              active: true, 
              message: 'Download cancelled by user.', 
              level: 'alert-info', 
              timeout: 3000 
            })
            return
          }
        }
        
        // Process CSV data with image paths
        const items = []
        const itemHeaders = []
        const imageUrls = [] // Store all image URLs for download
        
        geoJsonData.features.forEach((feature, index) => {
          const jsonObject = { ...feature.properties }
          
          // Format timestamps
          if (jsonObject.createdOn) {
            const date = jsonObject.createdOn * 1000
            jsonObject.createdOn = moment(date).format('YYYY-MM-DD HH:mm:ss')
          }
          if (jsonObject.updatedOn) {
            const date = jsonObject.updatedOn * 1000
            jsonObject.updatedOn = moment(date).format('YYYY-MM-DD HH:mm:ss')
          }
          
          // Format date and time fields
          if (this.$store.state.dataset.dynamic) {
            this.$store.state.collector.fields.forEach(field => {
              if (field.type === 'Date and Time' && feature.properties[field.label]) {
                const headerDate = feature.properties[field.label] * 1000
                if (field.includeTime) {
                  feature.properties[field.label] = moment(headerDate).format('YYYY-MM-DD HH:mm:ss')
                } else {
                  feature.properties[field.label] = moment(headerDate).format('YYYY-MM-DD')
                }
              }
            })
          }
          
          // Add coordinates
          jsonObject.longitude = feature.geometry.coordinates[0]
          jsonObject.latitude = feature.geometry.coordinates[1]
          
          // Process image fields
          if (this.$store.state.dataset.dynamic) {
            this.$store.state.collector.fields.forEach(field => {
              if (field.type === 'Image' || field.type === 'Computer Vision') {
                if (jsonObject[field.label] && jsonObject[field.label].length > 0) {
                  // Create relative path for CSV
                  const imagePath = 'images/' + (index + 1) + '_' + field.label.toLowerCase() + '/'
                  
                  // Store image URLs for download BEFORE changing the value
                  const originalImageArray = jsonObject[field.label]
                  const imageFilePaths = [] // Store full paths for CSV
                  
                  originalImageArray.forEach((image, imgIndex) => {
                    if (image.path) {
                      const imageFileName = (imgIndex + 1) + '.jpg'
                      const fullImagePath = imagePath + imageFileName
                      
                      imageUrls.push({
                        url: image.path,
                        localPath: fullImagePath,
                        type: 'original'
                      })
                      
                      // Add full path to CSV
                      imageFilePaths.push(fullImagePath)
                    }
                  })
                  
                  // Now set the full paths for CSV
                  jsonObject[field.label] = imageFilePaths.length > 0 ? imageFilePaths.join('; ') : 'No images collected'
                } else {
                  jsonObject[field.label] = 'No images collected'
                }
              }
            })
          }
          
          // Track headers
          const jsonKeys = Object.keys(jsonObject)
          jsonKeys.forEach(key => {
            if (!itemHeaders.includes(key)) {
              itemHeaders.push(key)
            }
          })
          
          items.push(jsonObject)
        })
        
        // Ensure all headers exist in each record
        items.forEach(item => {
          itemHeaders.forEach(header => {
            if (!item[header]) {
              item[header] = null
            }
          })
        })
        
        // Generate CSV
        const csv = Papa.unparse(items)
        
        // Create ZIP file with CSV and images
        const zip = new JSZip()
        
        // Add CSV to zip
        const cleanDescription = this.$store.state.dataset.file.originalFileName || 
                                this.$store.state.dataset.name.replace(/\s+/g, '_')
        zip.file(cleanDescription + '_with_images.csv', csv)
        
        // Add README file
        const readmeContent = 'Dataset: ' + this.$store.state.dataset.name + '\n' +
'Downloaded: ' + new Date().toISOString() + '\n' +
'Total Records: ' + items.length + '\n' +
'Total Images: ' + imageUrls.length + '\n' +
'\n' +
'This download contains:\n' +
'1. CSV file with all data including relative image paths\n' +
'2. Images folder with original high-resolution photos\n' +
'3. Image paths in CSV point to local image files\n' +
'\n' +
'Image paths in the CSV contain full file paths relative to this folder structure.\n' +
'For example: "images/1_image/1.jpg" refers to the specific image file in the download.\n' +
'Multiple images are separated by semicolons: "images/1_image/1.jpg; images/1_image/2.jpg"\n' +
'\n' +
'Only original high-resolution images are included for maximum quality and efficiency.\n' +
'File naming: 1.jpg, 2.jpg, etc. for each image in the folder.\n'
        zip.file('README.txt', readmeContent)
        
        // Download images in batches to handle large datasets
        const batchSize = 10 // Download 10 images at a time
        let successfulDownloads = 0
        let failedDownloads = 0
        
        for (let i = 0; i < imageUrls.length; i += batchSize) {
          const batch = imageUrls.slice(i, i + batchSize)
          const batchNumber = Math.floor(i / batchSize) + 1
          const totalBatches = Math.ceil(imageUrls.length / batchSize)
          
          // Update progress message
          this.$store.commit('setAlert', { 
            active: true, 
            message: 'Downloading images batch ' + batchNumber + '/' + totalBatches + ' (' + (i + 1) + '-' + Math.min(i + batchSize, imageUrls.length) + ' of ' + imageUrls.length + ')...', 
            level: 'alert-info', 
            timeout: 0 
          })
          
          // Process batch
          const batchPromises = batch.map(async (imageInfo) => {
            try {
              const imageRef = firebase.storage().ref(imageInfo.url)
              const imageUrl = await imageRef.getDownloadURL()
              const imageResponse = await fetch(imageUrl)
              const imageBlob = await imageResponse.blob()
              
              // Create folder structure in zip
              const folderPath = imageInfo.localPath.split('/').slice(0, -1).join('/')
              if (folderPath) {
                zip.folder(folderPath)
              }
              
              zip.file(imageInfo.localPath, imageBlob)
              return true
            } catch (error) {
              console.error('Failed to download image ' + imageInfo.url + ':', error)
              return false
            }
          })
          
          const batchResults = await Promise.all(batchPromises)
          successfulDownloads += batchResults.filter(result => result).length
          failedDownloads += batchResults.filter(result => !result).length
          
          // Small delay between batches to prevent overwhelming the server
          if (i + batchSize < imageUrls.length) {
            await new Promise(resolve => setTimeout(resolve, 100))
          }
        }
        
        // Generate and download zip file
        const zipBlob = await zip.generateAsync({ type: 'blob' })
        const zipUrl = URL.createObjectURL(zipBlob)
        
        const a = document.createElement('a')
        a.href = zipUrl
        a.download = cleanDescription + '_with_images.zip'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        
        // Clean up
        URL.revokeObjectURL(zipUrl)
        
        // Show success message
        let message = 'Download complete! ' + successfulDownloads + '/' + imageUrls.length + ' images downloaded successfully.'
        if (failedDownloads > 0) {
          message += ' ' + failedDownloads + ' images failed to download.'
        }
        
        this.$store.commit('setAlert', { 
          active: true, 
          message: message, 
          level: failedDownloads > 0 ? 'alert-warning' : 'alert-success', 
          timeout: 5000 
        })
        
      } catch (error) {
        console.error('Error in downloadWithImages:', error)
        this.$store.commit('setAlert', { 
          active: true, 
          message: 'Error downloading CSV + Images. Please try again.', 
          level: 'alert-danger', 
          timeout: 5000 
        })
      }
    }
  },
  filters: {
    dateTime (val) {
      if (!val) { return '-' }
      const date = val.toDate()
      return moment(date).format('MMMM Do YYYY, h:mm a')
    }
  }
}
</script>

<style scoped>
.download_link {
  color: #056839;
}

.download_link:hover {
  text-decoration: underline;
}

.input-group .form-control[readonly] {
  background-color: #f8f9fa;
  border-color: #ced4da;
}

.input-group .form-control[readonly]:focus {
  background-color: #f8f9fa;
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.card-top-nav {
  position:absolute;
  top: 100px;
  z-index:50;
  background-color:white;
  width:100%;
  left:0;
  background-color: #eee;
}
.card-top-nav .nav-tabs .nav-link {
  border-radius: 0;
  background-color: #eee;
  color: #333;
}

.card-top-nav .nav-link.active {
  background-color: #ddd;
}

.info_toggle {
  position: fixed;
  bottom: 50%;
  left: 10px;
  transition: all 0.2s ease-in-out;
  z-index: 1
}

.mobile-toggle {
  display: none;
}
@media(max-width:800px) {
  .card-top-nav {
    top: 90px;
  }
  .mobile-toggle {
    display: block;
  }
  .desktop-toggle {
    display: none;
  }
  .info_toggle {
    left: calc(50% - 50px);
    bottom: 10px;
  }
}

</style>
